import { create } from 'zustand';
import type { UserScreenNode } from '@/shared/types/system';
import type { TabGroup, TabGroupColor, TabModel } from './model/types';
import { tabLabel } from './model/types';
import {
  activeAfterCollapse,
  nextGroupColor,
  normalizeTabs,
  pruneGroups,
  sortTabs,
  type TabSortKey,
} from './lib/tabGroups';

export type { TabKind, TabModel, TabGroup, TabGroupColor } from './model/types';
export { tabLabel } from './model/types';

const STORAGE_KEY = 'octo.web.tabs.v2';
const MAX_ALIVE = 12;

interface TabsState {
  tabs: TabModel[];
  groups: TabGroup[];
  activeTabId: string | null; // null = ana sayfa
  /** Yan yana görünümde sağ panelde duran sekme; null = tek panel. */
  splitTabId: string | null;

  openScreen: (
    node: Pick<
      UserScreenNode,
      'screenId' | 'caption' | 'objectId' | 'screenTypeName' | 'iconId' | 'iconName'
    >,
    params?: Record<string, unknown>,
    opts?: { focusExisting?: boolean },
  ) => TabModel;
  openCustom: (tab: Omit<TabModel, 'tabId' | 'createdAt' | 'lastActiveAt'>) => TabModel;
  activate: (tabId: string | null) => void;
  close: (tabId: string) => void;
  closeOthers: (tabId: string) => void;
  closeRight: (tabId: string) => void;
  closeLeft: (tabId: string) => void;
  closeAll: () => void;
  rename: (tabId: string, caption: string | null) => void;
  reorder: (from: number, to: number) => void;
  /** Sürükle-bırak: sekmeyi hedef konuma taşır ve düştüğü yerin grubuna katar/çıkarır. */
  moveTab: (tabId: string, toIndex: number, groupId: string | null) => void;
  togglePin: (tabId: string) => void;
  duplicate: (tabId: string) => TabModel | null;
  sortBy: (key: TabSortKey) => void;

  /** Sekmeyi yeni bir gruba alır ve grubun kimliğini döndürür. */
  groupTab: (tabId: string) => string;
  moveToGroup: (tabId: string, groupId: string | null) => void;
  renameGroup: (groupId: string, name: string) => void;
  setGroupColor: (groupId: string, color: TabGroupColor) => void;
  toggleGroupCollapsed: (groupId: string) => void;
  /** Grubu dağıtır; sekmeler açık kalır. */
  ungroup: (groupId: string) => void;
  closeGroup: (groupId: string) => void;

  setSplit: (tabId: string | null) => void;
  hydrate: () => void;
}

type Persisted = Pick<TabsState, 'tabs' | 'groups' | 'activeTabId' | 'splitTabId'>;

function persist(state: Persisted) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        tabs: state.tabs,
        groups: state.groups,
        activeTabId: state.activeTabId,
        splitTabId: state.splitTabId,
      }),
    );
  } catch {
    /* noop */
  }
}

/**
 * Her mutasyon buradan geçer: şerit kuralları (sabitler başta, grup üyeleri bitişik) ve
 * artık kayıt temizliği (üyesiz grup, kapanmış split sekmesi) tek yerde uygulanır. Aksi
 * halde her eylemin bunları ayrı ayrı hatırlaması gerekirdi.
 */
function commit(patch: Partial<Persisted>, prev: Persisted): Persisted {
  const tabs = normalizeTabs(patch.tabs ?? prev.tabs);
  const groups = pruneGroups(tabs, patch.groups ?? prev.groups);
  const live = new Set(tabs.map((t) => t.tabId));
  const activeTabId = patch.activeTabId !== undefined ? patch.activeTabId : prev.activeTabId;
  const splitTabId = patch.splitTabId !== undefined ? patch.splitTabId : prev.splitTabId;
  const next: Persisted = {
    tabs,
    groups,
    // Aktif sekme kapandıysa ana sayfaya düşülür (çağıran yeni odağı zaten veriyorsa o geçerli).
    activeTabId: activeTabId && live.has(activeTabId) ? activeTabId : null,
    // Yan panel, sekmesi kapandığında ya da aktif sekmeyle aynı olduğunda kapanır.
    splitTabId: splitTabId && live.has(splitTabId) && splitTabId !== activeTabId ? splitTabId : null,
  };
  persist(next);
  return next;
}

export const useTabsStore = create<TabsState>((set, get) => {
  const apply = (patch: Partial<Persisted>) => set((s) => commit(patch, s));

  return {
    tabs: [],
    groups: [],
    activeTabId: null,
    splitTabId: null,

    openScreen: (node, params = {}, opts = {}) => {
      const uniqueKey = `screen:${node.screenId}`;
      const existing = get().tabs.find((t) => t.uniqueKey === uniqueKey);
      if (existing && opts.focusExisting !== false) {
        const merged = { ...existing, params: { ...existing.params, ...params }, lastActiveAt: Date.now() };
        apply({
          tabs: get().tabs.map((t) => (t.tabId === existing.tabId ? merged : t)),
          activeTabId: existing.tabId,
        });
        return merged;
      }
      // Aynı ekranın ikinci sekmesi (focusExisting: false) ayrı anahtar alır: aksi hâlde
      // menüden tekrar açıldığında bu kopyaya odaklanılabilirdi (bkz. duplicate).
      const copies = existing
        ? get().tabs.filter((t) => t.uniqueKey === uniqueKey || t.uniqueKey.startsWith(`${uniqueKey}#`)).length
        : 0;
      const tab: TabModel = {
        tabId: crypto.randomUUID(),
        kind: 'screen',
        screenId: node.screenId,
        objectId: node.objectId,
        screenTypeName: node.screenTypeName,
        caption: node.caption,
        iconId: node.iconId,
        iconName: node.iconName,
        params,
        createdAt: Date.now(),
        lastActiveAt: Date.now(),
        uniqueKey: copies ? `${uniqueKey}#${copies}` : uniqueKey,
      };
      apply({ tabs: [...get().tabs, tab], activeTabId: tab.tabId });
      return tab;
    },

    openCustom: (partial) => {
      const existing = get().tabs.find((t) => t.uniqueKey === partial.uniqueKey);
      if (existing) {
        apply({ activeTabId: existing.tabId });
        return existing;
      }
      const tab: TabModel = {
        ...partial,
        tabId: crypto.randomUUID(),
        createdAt: Date.now(),
        lastActiveAt: Date.now(),
      };
      apply({ tabs: [...get().tabs, tab], activeTabId: tab.tabId });
      return tab;
    },

    activate: (tabId) => {
      const tabs = tabId
        ? get().tabs.map((t) => (t.tabId === tabId ? { ...t, lastActiveAt: Date.now() } : t))
        : get().tabs;
      apply({ tabs, activeTabId: tabId });
    },

    close: (tabId) => {
      const { tabs, activeTabId } = get();
      const idx = tabs.findIndex((t) => t.tabId === tabId);
      if (idx < 0) return;
      const next = tabs.filter((t) => t.tabId !== tabId);
      let active = activeTabId;
      if (activeTabId === tabId) active = next[Math.min(idx, next.length - 1)]?.tabId ?? null;
      apply({ tabs: next, activeTabId: active });
    },
    closeOthers: (tabId) =>
      apply({ tabs: get().tabs.filter((t) => t.tabId === tabId || t.pinned), activeTabId: tabId }),
    closeRight: (tabId) => {
      const idx = get().tabs.findIndex((t) => t.tabId === tabId);
      apply({ tabs: get().tabs.filter((t, i) => i <= idx || t.pinned), activeTabId: tabId });
    },
    closeLeft: (tabId) => {
      const idx = get().tabs.findIndex((t) => t.tabId === tabId);
      apply({ tabs: get().tabs.filter((t, i) => i >= idx || t.pinned), activeTabId: tabId });
    },
    closeAll: () => apply({ tabs: get().tabs.filter((t) => t.pinned), activeTabId: null }),

    rename: (tabId, caption) => {
      const trimmed = caption?.trim() ?? '';
      apply({
        tabs: get().tabs.map((t) => (t.tabId === tabId ? { ...t, customCaption: trimmed || null } : t)),
      });
    },

    reorder: (from, to) => {
      const arr = [...get().tabs];
      const [item] = arr.splice(from, 1);
      if (item) arr.splice(to, 0, item);
      apply({ tabs: arr });
    },

    moveTab: (tabId, toIndex, groupId) => {
      const tabs = get().tabs;
      const from = tabs.findIndex((t) => t.tabId === tabId);
      if (from < 0) return;
      const moving = { ...tabs[from]!, groupId, pinned: groupId ? false : tabs[from]!.pinned };
      const without = tabs.filter((t) => t.tabId !== tabId);
      const insertAt = Math.max(0, Math.min(without.length, toIndex > from ? toIndex - 1 : toIndex));
      without.splice(insertAt, 0, moving);
      apply({ tabs: without });
    },

    togglePin: (tabId) =>
      apply({ tabs: get().tabs.map((t) => (t.tabId === tabId ? { ...t, pinned: !t.pinned } : t)) }),

    duplicate: (tabId) => {
      const src = get().tabs.find((t) => t.tabId === tabId);
      if (!src) return null;
      // Kopyanın uniqueKey'i farklı olmalı; aynı olsaydı menüden tekrar açıldığında
      // kopyaya odaklanır, kullanıcı iki ayrı pencere yerine tek sekme görürdü.
      const copies = get().tabs.filter(
        (t) => t.uniqueKey === src.uniqueKey || t.uniqueKey.startsWith(`${src.uniqueKey}#`),
      ).length;
      const copy: TabModel = {
        ...src,
        tabId: crypto.randomUUID(),
        uniqueKey: `${src.uniqueKey.split('#')[0]}#${copies}`,
        pinned: false,
        createdAt: Date.now(),
        lastActiveAt: Date.now(),
      };
      const idx = get().tabs.findIndex((t) => t.tabId === tabId);
      const arr = [...get().tabs];
      arr.splice(idx + 1, 0, copy);
      apply({ tabs: arr, activeTabId: copy.tabId });
      return copy;
    },

    sortBy: (key) => apply({ tabs: sortTabs(get().tabs, key, tabLabel) }),

    groupTab: (tabId) => {
      const groupId = crypto.randomUUID();
      const group: TabGroup = { groupId, name: '', color: nextGroupColor(get().groups), collapsed: false };
      apply({
        tabs: get().tabs.map((t) => (t.tabId === tabId ? { ...t, groupId, pinned: false } : t)),
        groups: [...get().groups, group],
      });
      return groupId;
    },

    moveToGroup: (tabId, groupId) =>
      apply({
        tabs: get().tabs.map((t) =>
          t.tabId === tabId ? { ...t, groupId, pinned: groupId ? false : t.pinned } : t,
        ),
      }),

    renameGroup: (groupId, name) =>
      apply({ groups: get().groups.map((g) => (g.groupId === groupId ? { ...g, name } : g)) }),
    setGroupColor: (groupId, color) =>
      apply({ groups: get().groups.map((g) => (g.groupId === groupId ? { ...g, color } : g)) }),

    toggleGroupCollapsed: (groupId) => {
      const { groups, tabs, activeTabId } = get();
      const group = groups.find((g) => g.groupId === groupId);
      if (!group) return;
      const collapsing = !group.collapsed;
      apply({
        groups: groups.map((g) => (g.groupId === groupId ? { ...g, collapsed: collapsing } : g)),
        // Katlanan grupta aktif sekme varsa odak dışarı taşınır, yoksa görünmez bir
        // sekme aktif kalır ve şeritte hiçbir sekme seçili görünmez.
        activeTabId: collapsing ? activeAfterCollapse(tabs, groupId, activeTabId) : activeTabId,
      });
    },

    ungroup: (groupId) =>
      apply({ tabs: get().tabs.map((t) => (t.groupId === groupId ? { ...t, groupId: null } : t)) }),
    closeGroup: (groupId) => {
      const remaining = get().tabs.filter((t) => t.groupId !== groupId);
      const active = get().activeTabId;
      const stillThere = remaining.some((t) => t.tabId === active);
      apply({
        tabs: remaining,
        activeTabId: stillThere ? active : (remaining[remaining.length - 1]?.tabId ?? null),
      });
    },

    setSplit: (tabId) => apply({ splitTabId: tabId }),

    hydrate: () => {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw) as Partial<Persisted>;
        if (!Array.isArray(parsed.tabs)) return;
        set(
          commit(
            {
              tabs: parsed.tabs,
              groups: Array.isArray(parsed.groups) ? parsed.groups : [],
              activeTabId: parsed.activeTabId ?? null,
              splitTabId: parsed.splitTabId ?? null,
            },
            { tabs: [], groups: [], activeTabId: null, splitTabId: null },
          ),
        );
      } catch {
        /* noop */
      }
    },
  };
});

/** Bellek: en eski pasif sekmeler "dondurulur" (unmount) — pane sayısı sınırı. Saf fonksiyon (useMemo ile kullanılır). */
export function frozenTabIds(
  tabs: TabModel[],
  activeTabId: string | null,
  splitTabId: string | null = null,
): Set<string> {
  if (tabs.length <= MAX_ALIVE) return new Set();
  const inactive = tabs
    .filter((t) => t.tabId !== activeTabId && t.tabId !== splitTabId && !t.pinned)
    .sort((a, b) => a.lastActiveAt - b.lastActiveAt);
  return new Set(inactive.slice(0, tabs.length - MAX_ALIVE).map((t) => t.tabId));
}
